import { parseImportFile } from '../../utils/collectionImport';
import { sendRequestViaTauri } from '../../utils/tauri';

/**
 * The AI-facing side of collection import (18.08.2026).
 *
 * "Point it at a spec and get a working collection" is the demo that sells an
 * API client, and every piece of it already existed — the parsers, the
 * detector, the apply path — with no way for the assistant to reach any of it.
 * This is the seam, not a second implementation: it calls the SAME
 * `parseImportFile` the Import dialog uses, so OpenAPI 3, Swagger 2, Postman,
 * Insomnia, HAR, Bruno (request files and whole-collection exports) and
 * Hoppscotch all work here the moment they work there. A Bruno collection
 * FOLDER is many files and only the dialog can take it. Environments that
 * come with a Bruno export are NOT imported on this path (the model never
 * writes variable values); `summarizeImport` says so.
 *
 * Two inputs because the model has no filesystem: `url` (fetch it — the common
 * case, since public specs are URLs) or `content` (the user pasted it).
 */

/** A spec big enough to matter, small enough not to wedge the app. */
const MAX_SPEC_BYTES = 5 * 1024 * 1024;

/** Filename hint from a URL, so the parser's YAML/JSON detection still works. */
function filenameFromUrl(url) {
  try {
    const path = new URL(url).pathname;
    const last = path.split('/').filter(Boolean).pop() || '';
    return /\.(json|ya?ml|har|bru)$/i.test(last) ? last : '';
  } catch {
    return '';
  }
}

/**
 * Fetch and parse a spec, returning a SUMMARY plus the parsed collections.
 *
 * The summary is what goes back to the model; the collections go to the apply
 * path. Keeping them separate is deliberate — a parsed OpenAPI document can be
 * thousands of endpoints, and feeding that back would blow the context window
 * on the user's own API key for no benefit.
 */
export async function fetchAndParseSpec({ url, content, filename } = {}) {
  let text = content;
  let hintName = filename || '';

  if (!text) {
    if (!url) return { error: 'Provide either url or content' };
    let res;
    try {
      res = await sendRequestViaTauri({ method: 'GET', url, timeout_secs: 60 });
    } catch (e) {
      return { error: `Could not fetch the spec: ${e?.message || String(e)}` };
    }
    if (!res || res.status >= 400) {
      return { error: `Fetching the spec returned ${res?.status ?? 'no response'}` };
    }
    text = typeof res.data === 'string' ? res.data : JSON.stringify(res.data ?? '');
    hintName = hintName || filenameFromUrl(url);
  }

  if (typeof text !== 'string' || !text.trim()) {
    return { error: 'The spec is empty' };
  }
  if (text.length > MAX_SPEC_BYTES) {
    return { error: `The spec is ${Math.round(text.length / 1024 / 1024)} MB; the limit is 5 MB` };
  }

  let parsed;
  try {
    parsed = parseImportFile(text, hintName);
  } catch (e) {
    // The parsers throw user-readable messages ("Unrecognized format…") —
    // pass them through rather than replacing them with a generic one.
    return { error: e?.message || 'Could not parse the spec' };
  }

  return { parsed };
}

/** Model-facing summary of what an import produced. Counts and names only. */
export function summarizeImport(parsed) {
  const collections = parsed.collections || [];
  return {
    format: parsed.formatLabel || parsed.format,
    collections: collections.map((c) => ({
      name: c.name,
      request_count: countRequests(c),
      // A few sample paths let the model describe what it imported and pick
      // sensible follow-up work, without shipping the whole endpoint list.
      sample_requests: sampleRequests(c, 8),
    })),
    warnings: [
      ...((parsed.environments || []).length
        ? [`${parsed.environments.length} environment(s) in the file were not imported; the user can import them with Import in the app.`]
        : []),
      ...(parsed.warnings || []),
    ].slice(0, 10),
  };
}

function countRequests(node) {
  let total = (node.requests || []).length;
  for (const folder of node.folders || []) total += countRequests(folder);
  return total;
}

function sampleRequests(node, limit, acc = []) {
  for (const r of node.requests || []) {
    if (acc.length >= limit) return acc;
    acc.push(`${r.method || 'GET'} ${r.url || r.name || ''}`.trim());
  }
  for (const folder of node.folders || []) {
    if (acc.length >= limit) return acc;
    sampleRequests(folder, limit, acc);
  }
  return acc;
}
